import {get} from "lodash";

/**
 * A getter function that returns the content of a node/box
 * @example
 * const getBoxContent = (box) => {
 *  if (!box) return null;
 *  return currentContent[box] ?? null;
 * }
 * @typedef {(box: string) => null | Object} BoxNodeGetter
 */

// a util class for tree data structure
// a tree is a data structure that consists of nodes in a parent / child relationship
// tree has a walk method that traverses the tree and executes a callback on each node

class TreeNode {
  constructor(content, processedPaths) {
    // content is String than it is a box, otherwise it is a content with property box
    this.box = typeof content === 'string' ? content : content?.box;

    /**
     * @type {TreeNode[]}
     */
    this.children = [];
    /**
     * @type {{box: string, index: number}[]}
     */
    this.parents = [];

    /**
     * @type {Set<string>}
     */
    this.processedPaths = processedPaths;
  }

  addParent(parent, index) {
    // parent with this box name already exists, return
    if (this.parents.find((p) => p.box === parent.box)) {
      return;
    }
    
    this.parents.push({ box: parent.box, index });
  }

  /**
   * @param {TreeNode | Object} content
   * @param {number} childIndex Where in the parent's index does this node exist?
   * @returns {TreeNode}
   */
  appendChild(content, childIndex) {
    content = content instanceof TreeNode
      ? content
      : new TreeNode(content, this.processedPaths);

    /**
     * If an earlier path lead here, it would have already been populated.
     * We also need the index, because we can still have A -> B, but A
     * has option 1, 2, 3, 4 and 5 that all leads to B.
     */
    if (!this.processedPaths.has(`${content.box}[${childIndex}] -> ${this.box}`)) {
      this.processedPaths.add(`${content.box}[${childIndex}] -> ${this.box}`);
      this.children.push(content);
    }
    if (!this.processedPaths.has(`${this.box} -> ${content.box}[${childIndex}]`)) {
      this.processedPaths.add(`${this.box} -> ${content.box}[${childIndex}]`);
      content.addParent(this, this.children.length);
    }

    return content;
  }

  /**
   * @returns {TreeNode[]}
   */
  getChildren() {
    return this.children;
  }

  /**
   * @returns {string}
   */
  getBoxName() {
    return this.box;
  }

  /**
   * @param {TreeNode | string} node If string, it is treated as 'box', and `node` will be root
   * @param {string | null} [box]
   * @returns {null|TreeNode}
   */
  findNode(node, box) {
    // node is string
    if (typeof node === 'string') {
      return this.findNode(this, node);
    }

    if (node.box === box) {
      return node;
    }

    for (let i = 0; i < node.children.length; i += 1) {
      const found = this.findNode(node.children[i], box);
      if (found) {
        return found;
      }
    }

    return null;
  }
}

class Tree {
  constructor() {
    /**
     * @type {null | TreeNode}
     */
    this.root = null;
    /**
     * @type {TreeNode[]}
     */
    this.nodes = [];

    /**
     * Keeps track of which paths have been processed
     * @type {Set<string>}
     */
    this.processedPaths = new Set();
  }

  /**
   * set the root node of the tree
   * @param {Object | TreeNode} content
   * @returns {TreeNode}
   */
  setRoot(content) {
    // if content is from TreeNode, set it as root
    if (content instanceof TreeNode) {
      this.root = content;
      return this.root;
    }

    // if content is string or content object, create a new TreeNode
    this.root = new TreeNode(content, this.processedPaths);
    this.nodes.push(this.root);
    return this.root;
  }

  /**
   * @returns {TreeNode|null}
   */
  getRoot() {
    return this.root;
  }

  /**
   * build the tree from content store, getBoxContent is getter function, root has to be set
   * @param {Object | BoxNodeGetter} getBoxContent
   * @returns {void}
   */
  buildTree(getBoxContent) {
    // if getBoxContent is not a function, return
    if (typeof getBoxContent !== 'function' && typeof getBoxContent !== 'object') {
      return;
    } else if (typeof getBoxContent === 'object') {
      // generate standard getBoxContent
      const contents = _.cloneDeep(getBoxContent);

      getBoxContent = (box) => {
        if (!box || !contents[box]) return null;
        return contents[box];
      };
    }

    // if root is not set, return
    if (!this.root) {
      return;
    }

    this.nodes = [];

    // build tree
    this.buildNode(this.root, getBoxContent);

    this.processedPaths.clear();
  }

  /**
   * this method is use for building tree from array of nodes, like in forms
   * @param {TreeNode} node
   * @param {BoxNodeGetter} getBoxContent
   * @param {{box: string, next?: any, values?: {box?: string}[]}[]} childBoxes
   */
  buildNodeFromArray(node, getBoxContent, childBoxes) {
    // no box property, end the recursion
    if (!node.box) {
      return;
    }

    const nodeContent = getBoxContent(node.box);
    // no node content, end the recursion
    if (!nodeContent) {
      return;
    }

    // get the child box from childBoxes array
    const childBox = childBoxes.find((box) => box.box === nodeContent.box);

    // child box has next property for one child or values property for multiple children
    if (childBox?.next) {
      const nodeExists = this.nodes.find((n) => n.box === childBox.next);
      const newNode = node.appendChild(nodeExists || getBoxContent(childBox.next) || childBox.next, 0);
      if (!nodeExists) {
        this.nodes.push(newNode);
      }
      this.buildNodeFromArray(newNode, getBoxContent, childBoxes);
    } else if (childBox?.values && Array.isArray(childBox.values)) {

      /**
       * childBox.values is not guaranteed to be in the right order.
       * We can also not override the correction (childBox.values = fixed)
       * because it gets detected as a change when compared to live.
       */
      const childBoxValues = this.fixFormChildValuesOrder(childBox.values, nodeContent);
      
      let i = 0;
      for (const value of childBoxValues) {
        if (!value.box) continue;

        const nodeExists = this.nodes.find((n) => n.box === value.box);
        const newNode = node.appendChild(nodeExists || getBoxContent(value.box) || value.box, i);
        if (!nodeExists) {
          this.nodes.push(newNode);
        }

        i++;
        this.buildNodeFromArray(newNode, getBoxContent, childBoxes);
      }
    }

    // BotFormEmail, BotFormValidableInput, BotFormFile:
    if (['BotFormEmail', 'BotFormValidableInput', 'BotFormFile'].includes(nodeContent.template)) {
      // add as second child a end node.
      const endNode = node.appendChild('end');
      this.nodes.push(endNode);
      // add as third child a cancel node.
      const cancelNode = node.appendChild('cancel');
      this.nodes.push(cancelNode);
    }
  }

  // build node and its children
  buildNode(node, getBoxContent) {
    const nodeContent = getBoxContent(node.box);

    if (!nodeContent) {
      return;
    }

    // if node value has followup property, build its children
    if (nodeContent.followup && nodeContent.followup.box) {
      const nodeExists = this.nodes.find((n) => n.box === nodeContent.followup.box);
      const newNode = node.appendChild(nodeExists || getBoxContent(nodeContent.followup.box), 0);

      if (!nodeExists) {
        this.nodes.push(newNode);
        this.buildNode(newNode, getBoxContent);
      }
    } else if (nodeContent.followup && !nodeContent.followup.box) {
      node.appendChild('completion');
    } else if (nodeContent.answers && Array.isArray(nodeContent.answers)) {
      let i = 0;
      for (const answer of nodeContent.answers) {
        // copy answer to get two different objects
        const cloneOfAnswer = _.cloneDeep(answer);
        if (cloneOfAnswer.template === 'copy') {
          const copyFrom = nodeContent.answers.find((a, n) => {
            if (a.box && n !== i) {
              return true;
            }

            return false;
          });

          if (copyFrom) {
            cloneOfAnswer.box = copyFrom.box;
          }
        }

        if (!cloneOfAnswer.box) {
          node.appendChild('completion');
          continue;
        }

        const nodeExists = this.nodes.find((n) => n.box === cloneOfAnswer.box);
        const newNode = node.appendChild(nodeExists || getBoxContent(cloneOfAnswer.box), i);

        if (!nodeExists) {
          this.nodes.push(newNode);
          this.buildNode(newNode, getBoxContent);
        }

        i++;
      }
    } else {
      // handling forms
      if (nodeContent.childBoxes && Array.isArray(nodeContent.childBoxes)) {
        // get start child box
        const startChildBox = nodeContent.childBoxes.find((box) => box.box === nodeContent.start.next);

        if (nodeContent.childBoxes.length === 0 && nodeContent.completion || !startChildBox) {
          const completionNode = node.appendChild('completion');
          this.nodes.push(completionNode);
        } else {
          const startChildNodeContent = getBoxContent(startChildBox.box);

          // cause the botforms has the knowledge of all child nodes but not the content
          if (!startChildNodeContent) {
            return;
          }

          const nodeExists = this.nodes.find((n) => n.box === startChildNodeContent.box);
          const newNode = node.appendChild(nodeExists || getBoxContent(startChildNodeContent.box), this.nodes.indexOf(nodeExists));
          if (!nodeExists) {
            this.nodes.push(newNode);
          }

          this.buildNodeFromArray(newNode, getBoxContent, nodeContent.childBoxes);
        }
      }
    }

    // SimpleInformation: no branch node
    if (nodeContent.template === 'SimpleInformation') {
      // just add as second child a completion node.
      const completionNode = node.appendChild('completion');
      this.nodes.push(completionNode);
    }
  }

  // insert a node into the tree
  insert(box, parent) {
    // if parent is instance of TreeNode set it as parent otherwise find it
    const parentNode = parent instanceof TreeNode ? parent : this.findNode(parent);

    if (parentNode) {
      // check if the box is present at path between parent and root
      const path = this.getPath(parentNode.box);
      if (path.includes(box)) {
        // attention circular, return null
        return null;
      }

      return parentNode.appendChild(box);
    }

    // if parent is not found, return null
    return null;
  }

  /**
   * @param {TreeNode | string} node If string, it is treated as 'box', and `node` will be root
   * @param {string | null} [box]
   */
  findNode(node, box) {
    // node is string
    if (typeof node === 'string') {
      return this.root.findNode(node);
    }

    if (!node) {
      return null;
    }

    node.findNode(node, box);
  }

  /**
   * @param {?string} box
   * @param {Record<string, {question: number}>} selectedNodes
   * @returns {string[]}
   */
  getCurrentPath(box, selectedNodes = {}) {
    // walk the tree and find with the box and walk along the selectedNodes { [box]: { [property]: index } }
    const path = [];
    this.getCurrentPathNode(this.root, box, selectedNodes, path);
    return path.reverse();
  }

  /**
   * @param {TreeNode} node
   * @param {?string} box
   * @param {Record<string, {question: number}>} selectedNodes
   * @param {string[]} path
   * @returns {boolean}
   */
  getCurrentPathNode(node, box, selectedNodes, path) {
    if (node?.box === box) {
      path.push(node.box);
      return true;
    }

    if (node.children.length === 0 && box) {
      return false;
    } else if (node.children.length === 0) {
      path.push(node.box);
      return true;
    }

    if (node.children.length === 1) {
      const found = this.getCurrentPathNode(node.children[0], box, selectedNodes, path);
      if (found) {
        path.push(node.box);
        return true;
      }
    }

    if (node.children.length > 1) {
      if (selectedNodes[node.box]) {
        let selectedOption = selectedNodes[node.box].question || 0;

        if (selectedOption >= node.children.length) {
          selectedOption = node.children.length - 1;
        }

        const found = this.getCurrentPathNode(node.children[selectedOption], box, selectedNodes, path);
        if (found) {
          path.push(node.box);
          return true;
        }
      } else {
        const found = this.getCurrentPathNode(node.children[0], box, selectedNodes, path);
        if (found) {
          path.push(node.box);
          return true;
        }
      }
    }

    return false;
  }

  getPath(box) {
    const path = [];
    this.getPathNode(this.root, box, path);
    return path;
  }

  getPathNode(node, box, path) {
    if (node.box === box) {
      path.push(node.box);
      return true;
    }

    for (let i = 0; i < node.children.length; i += 1) {
      const found = this.getPathNode(node.children[i], box, path);
      if (found) {
        path.push(node.box);
        return true;
      }
    }

    return false;
  }

  remove(box) {
    return this.removeNode(this.root, box);
  }

  removeNode(node, box) {
    if (node.children.length === 0) {
      return;
    }

    for (let i = 0; i < node.children.length; i += 1) {
      if (node.children[i].box === box) {
        node.children.splice(i, 1);
        return node;
      }
      return this.removeNode(node.children[i], box);
    }
  }

  getAllNodesWithDepthNode(node, result, depth) {
    result.push({ box: node.box, depth });
    for (let i = 0; i < node.children.length; i += 1) {
      this.getAllNodesWithDepthNode(node.children[i], result, depth + 1);
    }
  }

  getAllNodesWithDepth() {
    const result = [];
    this.getAllNodesWithDepthNode(this.root, result, 0);
    // sort results by depth from low to high
    result.sort((a, b) => a.depth - b.depth);

    return result;
  }

  print(printFunc = (parentNode, node) => `${node.box} ->`) {
    return this.printNode(null, this.root, printFunc);
  }

  printNode(parentNode, node, printFunc) {
    let nodes = [];
    if (!node) return nodes;

    nodes.push(printFunc(parentNode, node));

    for (let i = 0; i < node.children.length; i += 1) {
      nodes = nodes.concat(this.printNode(node, node.children[i], printFunc));
    }
    return nodes;
  }

  fixFormChildValuesOrder(valuesArray, node) {
    if (node.loading) return valuesArray; // No need to process if not loaded
    /**
     * @type {({intent: string} & Object)[]}
     */
    let arrayOfIntents = null;

    if (node.template === 'BotFormConditionalSwitch') {
      // Special case: BotFormConditionalSwitch stores it directly in root
      arrayOfIntents = node.answers;
    } else {
      // Most cases
      const found = node?.question?.actions?.findLast(box => !!box?.content?.quick);
      if (found) arrayOfIntents = found.content.quick;
    }

    // We can't do anything without this
    if (!arrayOfIntents) return valuesArray;

    // Clone the array
    const candidates = Array.from(valuesArray);
    /**
     * Due to some complexity with an intent being referenced many times, and at different index positions,
     * we must use a queue to pull the desired item from, and then make sure we never re-visit it.
     *
     * arrayOfIntents is the source of truth for order, and valuesArray is our candidates
     * E.g. valuesArray = [A, B, C, A, D]
     *   arrayOfIntents = [A, B, A, C, D]
     *
     * If we just use sort, valuesArray's A will always end up in the first position.
     */

    const newArray = [];

    // Sort it based on arrayOfIntents
    for (let i=0; i < arrayOfIntents.length; i++) {
      const intent = arrayOfIntents[i].intent;
      const index = candidates.findIndex((v) => v.value === intent);
      if (index === -1) continue; // Nothing we can do, but should never really happen

      // Get the item from the queue
      newArray.push( candidates.splice(index, 1)[0] );
    }

    return newArray;
  }
}

// export Tree and TreeNode
export { Tree, TreeNode };